import React, { useState } from 'react';
import { useCart } from '../context/CartContext';
import { X, CreditCard, AlertCircle } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CheckoutForm = ({ onClose, total }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { state, dispatch } = useCart();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cityError, setCityError] = useState(null);
  const [zipError, setZipError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    streetAddress: '',
    apartmentUnit: '',
    city: '',
    zipCode: '',
    deliveryDate: '',
    notes: ''
  });

  // List of valid Miami zip codes
  const miamiZipCodes = [
    '33101', '33102', '33106', '33109', '33111', '33114', '33116', '33122', 
    '33124', '33125', '33126', '33127', '33128', '33129', '33130', '33131', 
    '33132', '33133', '33134', '33135', '33136', '33137', '33138', '33139', 
    '33140', '33141', '33142', '33143', '33144', '33145', '33146', '33147', 
    '33149', '33150', '33151', '33152', '33153', '33155', '33156', '33157', 
    '33158', '33159', '33160', '33161', '33162', '33163', '33164', '33165', 
    '33166', '33167', '33168', '33169', '33170', '33172', '33173', '33174', 
    '33175', '33176', '33177', '33178', '33179', '33180', '33181', '33182', 
    '33183', '33184', '33185', '33186', '33187', '33188', '33189', '33190', 
    '33193', '33194', '33196'
  ];

  const getMinDeliveryDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 2); // Add 2 days to current date
    return date.toISOString().split('T')[0];
  };

  const handleCityBlur = () => {
    const cityValue = formData.city.trim().toLowerCase();
    if (!cityValue.includes('miami')) {
      setCityError('Delivery is only available in Miami');
    } else {
      setCityError(null);
    }
  };

  const handleZipBlur = () => {
    const zipValue = formData.zipCode.trim();
    if (!miamiZipCodes.includes(zipValue)) {
      setZipError('Please enter a valid Miami ZIP code');
    } else {
      setZipError(null);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Clear errors when user starts typing
    if (name === 'city') {
      setCityError(null);
    }
    if (name === 'zipCode') {
      setZipError(null);
    }
    
    if (name === 'deliveryDate') {
      const selectedDate = new Date(value);
      const minDate = new Date(getMinDeliveryDate());
      
      if (selectedDate < minDate) {
        setError('Please select a delivery date at least 2 days from today');
        return;
      }
    }
    
    setFormData({ ...formData, [name]: value });

    if (name === 'deliveryDate') {
      setError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate city and zip code before proceeding
    if (!formData.city.toLowerCase().includes('miami')) {
      setCityError('Delivery is only available in Miami');
      return;
    }
    
    if (!miamiZipCodes.includes(formData.zipCode)) {
      setZipError('Please enter a valid Miami ZIP code');
      return;
    }

    setLoading(true);
    setError(null);

    if (!stripe || !elements) {
      setError('Stripe has not been initialized');
      setLoading(false);
      return;
    }

    try {
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
        billing_details: {
          name: formData.name,
          email: formData.email,
        },
      });

      if (stripeError) {
        setError(stripeError.message);
        setLoading(false);
        return;
      }

      // Construct the full address
      const fullAddress = `${formData.streetAddress}${formData.apartmentUnit ? ', ' + formData.apartmentUnit : ''}, ${formData.city}, FL ${formData.zipCode}`;

      console.log('Submitting payment request...');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/create-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          paymentMethodId: paymentMethod.id,
          amount: total,
          customerDetails: {
            ...formData,
            address: fullAddress,
            deliveryDate: formData.deliveryDate
          },
          orderItems: state.items.map(item => ({
            name: item.name,
            quantity: item.quantity,
            price: typeof item.price === 'string' ? item.price : `$${item.price.toFixed(2)}`
          }))
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        // Handle server errors
        const errorMessage = result.error || result.message || 'Payment processing failed';
        console.error('Payment error:', errorMessage);
        setError(`Payment error: ${errorMessage}`);
        setLoading(false);
        return;
      }

      if (result.error) {
        setError(result.error);
        setLoading(false);
      } else {
        // Success path
        dispatch({ type: 'CLEAR_CART' });
        onClose();
        alert('Payment successful! You will receive a confirmation email shortly.');
      }
    } catch (error) {
      console.error('Payment submission error:', error);
      setError('Network error: Could not process your payment. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "mt-1 block w-full border border-[#E5DCC9] focus:outline-none focus:border-[#6B7C3F] bg-white px-3 py-2.5 text-[#3D2817]";
  const labelClass = "block text-xs tracking-[0.18em] uppercase text-[#3D2817]";

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className={labelClass}>Name</label>
        <input
          type="text"
          name="name"
          required
          value={formData.name}
          onChange={handleChange}
          className={inputClass}
        />
      </div>

      <div>
        <label className={labelClass}>Email</label>
        <input
          type="email"
          name="email"
          required
          value={formData.email}
          onChange={handleChange}
          className={inputClass}
        />
      </div>

      <div>
        <label className={labelClass}>Phone</label>
        <input
          type="tel"
          name="phone"
          required
          value={formData.phone}
          onChange={handleChange}
          className={inputClass}
        />
      </div>

      <div className="space-y-4">
        <div>
          <label className={labelClass}>Street Address</label>
          <input
            type="text"
            name="streetAddress"
            required
            value={formData.streetAddress}
            onChange={handleChange}
            className={inputClass}
            placeholder="e.g., 123 Main Street"
          />
        </div>

        <div>
          <label className={labelClass}>Apartment/Unit (Optional)</label>
          <input
            type="text"
            name="apartmentUnit"
            value={formData.apartmentUnit}
            onChange={handleChange}
            className={inputClass}
            placeholder="e.g., Apt 4B"
          />
        </div>

        <div>
          <label className={labelClass}>City</label>
          <input
            type="text"
            name="city"
            required
            value={formData.city}
            onChange={handleChange}
            onBlur={handleCityBlur}
            className={`mt-1 block w-full border focus:outline-none bg-white px-3 py-2.5 text-[#3D2817] ${
              cityError ? 'border-red-500' : 'border-[#E5DCC9] focus:border-[#6B7C3F]'
            }`}
            placeholder="Miami"
          />
          {cityError && (
            <div className="mt-2 flex items-center gap-2 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>{cityError}</span>
            </div>
          )}
        </div>

        <div>
          <label className={labelClass}>ZIP Code</label>
          <input
            type="text"
            name="zipCode"
            required
            value={formData.zipCode}
            onChange={handleChange}
            onBlur={handleZipBlur}
            className={`mt-1 block w-full border focus:outline-none bg-white px-3 py-2.5 text-[#3D2817] ${
              zipError ? 'border-red-500' : 'border-[#E5DCC9] focus:border-[#6B7C3F]'
            }`}
            placeholder="e.g., 33130"
            maxLength="5"
          />
          {zipError && (
            <div className="mt-2 flex items-center gap-2 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>{zipError}</span>
            </div>
          )}
        </div>
      </div>

      <div>
        <label className={labelClass}>Requested Delivery Date</label>
        <input
          type="date"
          name="deliveryDate"
          required
          value={formData.deliveryDate}
          onChange={handleChange}
          min={getMinDeliveryDate()}
          className={inputClass}
        />
        <p className="text-xs text-[#6B5644] mt-1">Minimum 2 days notice required</p>
      </div>

      <div>
        <label className={labelClass}>Special Notes</label>
        <textarea
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          rows="2"
          className={inputClass}
          placeholder="Any special requests or notes for your order..."
        />
      </div>

      <div className="mt-4">
        <label className={`${labelClass} mb-2`}>Card Details</label>
        <div className="border border-[#E5DCC9] bg-white p-3">
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#3D2817',
                  '::placeholder': {
                    color: '#6B5644',
                  },
                },
                invalid: {
                  color: '#9e2146',
                },
              },
            }}
          />
        </div>
      </div>

      {error && (
        <div className="text-red-600 text-sm">{error}</div>
      )}

      <div className="mt-4 border-t border-[#E5DCC9] pt-4">
        <div className="flex justify-between font-serif text-lg mb-4">
          <span>Total</span>
          <span>${total.toFixed(2)}</span>
        </div>
        <button
          type="submit"
          disabled={!stripe || loading}
          className={`w-full flex items-center justify-center gap-2 bg-[#6B7C3F] text-[#FAF6EE] py-4 tracking-[0.2em] uppercase text-sm hover:bg-[#5A6A35] transition-colors ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <CreditCard className="w-4 h-4" />
          {loading ? 'Processing…' : 'Pay Now'}
        </button>
      </div>
    </form>
  );
};

const Checkout = ({ onClose }) => {
  const { state } = useCart();

  const total = state.items.reduce((sum, item) => {
    const price = typeof item.price === 'string' 
      ? parseFloat(item.price.replace('$', '')) 
      : item.price;
    return sum + price * item.quantity;
  }, 0);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-[#3D2817]/40">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="relative transform overflow-hidden bg-[#FAF6EE] text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg text-[#3D2817]">
          <div className="px-6 pb-6 pt-6">
            <div className="flex justify-between items-center mb-6 pb-4 border-b border-[#E5DCC9]">
              <h3 className="font-serif text-xl tracking-wide">Checkout</h3>
              <button onClick={onClose} className="p-2 hover:bg-[#EFE7D6] rounded-full transition-colors" aria-label="Close checkout">
                <X className="h-5 w-5" />
              </button>
            </div>

            <Elements stripe={stripePromise}>
              <CheckoutForm onClose={onClose} total={total} />
            </Elements>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;